import type {
  Panel,
  PanelLayoutReport,
  PanelRow,
  Section,
} from "@electro-planner/domain";
import { buildPanelSld } from "./sld.ts";
import { buildPanelRailLayout } from "./railLayout.ts";

/**
 * PDF-export sker via webbläsarens utskriftsvy. Vi bygger ett rent HTML-dokument
 * i en osynlig ny flik, applicerar @page-regler och triggar print-dialogen.
 * Användaren väljer "Spara som PDF" i dialogen.
 *
 * Sida 1: centralspec-tabellen (beteckning, komponent, A, kabel, användning, last).
 * Sida 2: enlinjeschema (SLD) genererat från samma data.
 * Sida 3: DIN-skena-layout — modul-placering per skena.
 */
export function exportPanelToPdf({
  panel,
  sections,
  rows,
  layout,
}: {
  panel: Panel | undefined;
  sections: ReadonlyArray<Section>;
  rows: ReadonlyArray<PanelRow>;
  layout: PanelLayoutReport;
}): void {
  const w = window.open("", "_blank", "noopener");
  if (!w) {
    alert("Kunde inte öppna utskriftsfönstret — kontrollera pop-up-inställningar.");
    return;
  }

  // Gruppera per sektion + osektionerade sist, rendera gruppheader före
  // varje sektion så utskriften följer samma struktur som skärmen.
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);
  const rowsBySection = new Map<string, PanelRow[]>();
  const unsectioned: PanelRow[] = [];
  const sortedAll = [...rows].sort((a, b) => a.order - b.order);
  for (const r of sortedAll) {
    if (r.sectionId) {
      const arr = rowsBySection.get(r.sectionId) ?? [];
      arr.push(r);
      rowsBySection.set(r.sectionId, arr);
    } else {
      unsectioned.push(r);
    }
  }

  const emitRow = (r: PanelRow) => {
    const cable = formatCable(r);
    const specs = [
      r.ampRating !== undefined ? `${r.ampRating} A` : "",
      r.poles !== undefined ? `${r.poles}P` : "",
      r.characteristic ?? "",
    ]
      .filter(Boolean)
      .join(" ");
    const indent = r.indent ?? 0;
    const indentPad = indent > 0 ? ` style="padding-left: ${4 + indent * 10}px"` : "";
    const reviewedMark = r.reviewed ? "✓" : "";
    const desigDisplay = escape(r.designation);
    return `
      <tr>
        <td class="check">${reviewedMark}</td>
        <td${indentPad}>${desigDisplay}</td>
        <td>${escape(r.component ?? "")}</td>
        <td>${escape(specs)}</td>
        <td>${escape(cable)}</td>
        <td>${escape(r.cableNumber ?? "")}</td>
        <td>${escape(r.usage ?? "")}</td>
        <td>${escape(r.loadWatts !== undefined ? String(r.loadWatts) + " W" : "")}</td>
        <td>${escape(r.notes ?? "")}</td>
      </tr>`;
  };

  const sectionBlocks: string[] = [];
  for (const s of sortedSections) {
    const srows = rowsBySection.get(s.id) ?? [];
    if (srows.length === 0) continue;
    sectionBlocks.push(
      `<tr class="section"><td colspan="9"><strong>${escape(s.code)}</strong>${s.label ? ` · ${escape(s.label)}` : ""}</td></tr>`,
    );
    sectionBlocks.push(srows.map(emitRow).join(""));
  }
  if (unsectioned.length > 0) {
    if (sortedSections.length > 0) {
      sectionBlocks.push(
        `<tr class="section"><td colspan="9"><em>Ej sektionerade</em></td></tr>`,
      );
    }
    sectionBlocks.push(unsectioned.map(emitRow).join(""));
  }
  const rowHtml = sectionBlocks.join("");

  const title = `${panel?.designation ?? ""} ${panel?.title ?? "Central"}`.trim();

  // SLD på sida 2 — hoppas över om det inte finns några rader alls.
  const sldSvg = rows.length > 0 ? buildPanelSld({ panel, sections, rows }) : "";
  // DIN-layout på sida 3 — hoppas också över om det inte finns rader.
  const layoutSvg = rows.length > 0 ? buildPanelRailLayout({ panel, layout }) : "";

  const html = `<!DOCTYPE html>
<html lang="sv">
<head>
<meta charset="utf-8" />
<title>${escape(title || "Centralspec")}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; color: #111; font-size: 10pt; }
  header { border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 12px; }
  h1 { font-size: 16pt; margin: 0 0 4px; }
  .meta { font-size: 9pt; color: #555; }
  table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  th, td { border: 1px solid #888; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #eee; font-weight: 600; }
  td.check { text-align: center; color: #059669; font-weight: 700; width: 14px; }
  tbody tr.section td { background: #e6eef7; font-weight: 600; border-top: 2px solid #333; }
  .status-chip { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 8pt; font-weight: 600; margin-left: 6px; }
  .status-draft { background: #f1f5f9; color: #475569; }
  .status-review { background: #fef3c7; color: #92400e; }
  .status-done { background: #d1fae5; color: #065f46; }
  footer { margin-top: 16px; font-size: 8pt; color: #777; }
  .sld-page { page-break-before: always; margin: 0; padding: 0; }
  .sld-page h2 { font-size: 12pt; margin: 0 0 6px; }
  .sld-page .sld-meta { font-size: 8pt; color: #777; margin-bottom: 6px; }
  .sld-page svg { display: block; width: 100%; height: auto; }
</style>
</head>
<body>
  <header>
    <h1>${escape(title || "Centralspec")}${renderStatusChip(panel?.status)}</h1>
    <div class="meta">
      ${renderMetaGrid(panel)}
      Utskriven: ${new Date().toLocaleString("sv-SE")}
    </div>
  </header>

  <table>
    <thead>
      <tr>
        <th class="check">✓</th>
        <th>Bet.</th>
        <th>Komponent</th>
        <th>Spec</th>
        <th>Kabel</th>
        <th>Kabelnr</th>
        <th>Användning</th>
        <th>Last</th>
        <th>Notering</th>
      </tr>
    </thead>
    <tbody>${rowHtml}</tbody>
  </table>

  ${panel?.projectNotes ? `<section style="margin-top:12px;font-size:9pt"><strong>Anteckningar:</strong> ${escape(panel.projectNotes)}</section>` : ""}

  <footer>
    Genererad av electro-planner · Centralspec
  </footer>

  ${sldSvg ? `<section class="sld-page">
    <h2>Enlinjeschema — ${escape(title || "Centralspec")}</h2>
    <div class="sld-meta">Auto-genererat från centralspecifikationen. Huvudbrytare överst, sektioner och grenar hängande under.</div>
    ${sldSvg}
  </section>` : ""}

  ${layoutSvg ? `<section class="sld-page">
    <h2>Central-layout — ${escape(title || "Centralspec")}</h2>
    <div class="sld-meta">DIN-skenornas modul-placering. Färgkodning: blå=MCB, gul=RCD, grön=RCBO, röd=MCCB, grå=lastbrytare.</div>
    ${layoutSvg}
  </section>` : ""}

  <script>
    window.addEventListener("load", () => {
      setTimeout(() => window.print(), 200);
    });
  </script>
</body>
</html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
}

function formatCable(r: PanelRow): string {
  if (r.cableType && r.cableCores && r.cableArea !== undefined) {
    return `${r.cableType} ${r.cableCores}G${String(r.cableArea).replace(".", ",")}${
      r.cableLength ? ` · ${r.cableLength} m` : ""
    }`;
  }
  return r.cableType ?? "";
}

function escape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Liten färgad status-chip bredvid titeln. */
function renderStatusChip(status: Panel["status"]): string {
  if (!status || status === "draft") return "";
  const label = status === "review" ? "Granskning" : "Klar";
  return ` <span class="status-chip status-${status}">${label}</span>`;
}

/** Meta-grid i titelblock: två kolumner med projekt-metadata. */
function renderMetaGrid(panel: Panel | undefined): string {
  if (!panel) return "";
  const left: string[] = [];
  const right: string[] = [];
  if (panel.location) left.push(`Plats: ${escape(panel.location)}`);
  if (panel.enclosure) left.push(`Kapsling: ${escape(panel.enclosure)}`);
  if (panel.busbarAmps) left.push(`Huvudskena: ${panel.busbarAmps} A`);
  if (panel.earthingSystem) left.push(`Servis: ${escape(panel.earthingSystem)}`);
  if (panel.designer) right.push(`Konstruktör: ${escape(panel.designer)}`);
  if (panel.company) right.push(`Företag: ${escape(panel.company)}`);
  if (panel.phone) right.push(`Telefon: ${escape(panel.phone)}`);
  if (left.length === 0 && right.length === 0) return "";
  return `<div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin-bottom:4px">
    <div>${left.join("<br />")}</div>
    <div>${right.join("<br />")}</div>
  </div>`;
}
